import { all, call, put, select, take, takeLatest } from 'redux-saga/effects';
import { Action } from 'typescript-fsa';

import { getAuthProps } from '@topconagriculture/auth';
import { hideLoader, showLoader } from '@topconagriculture/loader';

import api from '../../../core/api';
import { ShareEditInvitation, SharingMessage } from '../../../core/api/sharing/sharing.types';
import { shareMutations } from '../../../core/graphql/share';
import { UpdateShareInput } from '../../../core/graphql/share/share.type';
import { LoadersNames } from '../../ui/ui.constants';
import * as actions from './sharing.actions';
import { notificationError, notificationSuccess } from '../../../core/helpers/utils/notificationUtils';
import { composeErrorMessage } from '../../../core/helpers/utils/utils';
import { getCompaniesList } from '../company/company.actions';
import { getFarmsListRequest } from '../farms/farms.actions';

export function* getSharesSaga() {
  yield put(showLoader(LoadersNames.fetchShares));
  try {
    const result = yield call(api.sharing.getShares);
    const { companyId } = yield select(getAuthProps);

    yield put(
      actions.getShares.done({
        params: companyId,
        result,
      }),
    );
  } catch (err) {
    yield put(notificationError('sharing:errors:cant_load_shares'));
  }
  yield put(hideLoader(LoadersNames.fetchShares));
}

export function* deleteSharePartnerSaga(
  action: ReturnType<typeof actions.deleteSharePartner.started> | ReturnType<typeof actions.leaveInvitation.started>,
) {
  const { id } = action.payload;
  try {
    const result = yield call(api.sharing.deleteShare, id);
    yield put(
      action.type === actions.deleteSharePartner.type
        ? actions.deleteSharePartner.done({
            params: action.payload,
            result,
          })
        : actions.leaveInvitation.done({
            params: action.payload,
            result,
          }),
    );
    yield put(notificationSuccess('sharing:partner_deleted'));
    yield take(actions.checkShares.done);
    if (action.type === actions.leaveInvitation.started.type) {
      yield put(getCompaniesList.started({}));
    }
  } catch (err) {
    yield put(notificationError('sharing:errors:cant_remove_partner'));
  }
}

export function* sendShareInviteSaga(action: ReturnType<typeof actions.sendShareInvite.started>) {
  const { data } = action.payload;
  const { inviteEmail, sharedItems, shareType, metadata, currentInvitationId } = data;
  if (!!currentInvitationId) {
    try {
      yield call(api.sharing.deleteShare, currentInvitationId);
    } catch (err) {
      yield put(notificationError('sharing:errors:cant_delete_previous_invitation'));
    }
    try {
      const result = yield call(api.sharing.postShare, {
        inviteEmail,
        sharedItems,
        shareType,
        metadata,
      });
      yield put(
        actions.sendShareInvite.done({
          params: currentInvitationId as any,
          result,
        }),
      );
      if (!!currentInvitationId) {
        yield put(notificationSuccess('success:share:invitation_resent', { inviteEmail }));
      }
    } catch (err) {
      if (err.status === 403) yield put(notificationError('sharing:errors:same_company'));
    }
  } else {
    try {
      const shares: SharingMessage[] = yield call(api.sharing.getShares);
      if (shares.find((elem) => elem.inviteEmail === inviteEmail)) {
        throw new Error('sharing:errors:user_has_invitation');
      }
      const result = yield call(api.sharing.postShare, {
        inviteEmail,
        sharedItems,
        shareType,
        metadata,
      });
      yield put(
        actions.sendShareInvite.done({
          params: inviteEmail as any,
          result,
        }),
      );
      yield call(getSharesSaga);
    } catch (err) {
      if (err.data && err.data.code === 'SameCompany') yield put(notificationError('sharing:errors:same_company'));
      else {
        yield put(notificationError(err));
      }
    }
  }
}

export function* editInviteSaga(action: Action<ShareEditInvitation>) {
  const { payload } = action;
  try {
    const result = yield call(shareMutations.updateShare, payload as UpdateShareInput);
    yield all([
      yield put(
        actions.editInvite.done({
          params: payload.id,
          result,
        }),
      ),
      yield put(notificationSuccess('sharing:edit_role_success')),
    ]);
  } catch (error) {
    yield put(notificationError('sharing:errors:cant_update_role'));
  }
}

export function* acceptInvitationSaga(action: ReturnType<typeof actions.acceptInvitation.started>) {
  const { id: inviteToken } = action.payload;
  try {
    const result = yield call(api.sharing.acceptInvitation, inviteToken);
    yield all([
      put(
        actions.acceptInvitation.done({
          params: action.payload,
          result,
        }),
      ),
      yield put(notificationSuccess('sharing:invitation_accepted')),
    ]);
    yield put(getFarmsListRequest.started({}));
    yield take(getFarmsListRequest.done);
    yield put(getCompaniesList.started({}));
  } catch (err) {
    if (err.status === 409) {
      yield put(notificationSuccess('sharing:invitation_already_accepted'));
      yield put(getFarmsListRequest.started({}));
      yield take(getFarmsListRequest.done);
      yield put(getCompaniesList.started({}));
    } else {
      yield all([
        yield put(notificationError('sharing:errors:cant_accept_invitation')),
        yield put(
          actions.acceptInvitation.failed({
            params: action.payload,
            error: err,
          }),
        ),
      ]);
    }
  }
}

export function* checkSharesSaga() {
  try {
    yield call(api.farms.checkShares);
    yield put(actions.checkShares.done({ result: {}, params: {} }));
  } catch (error) {
    yield put(notificationError(composeErrorMessage(error)));
  }
}

export function* watchSharingSaga() {
  yield takeLatest(actions.getShares.started, getSharesSaga);
  yield takeLatest(actions.getShares.done, checkSharesSaga);
  yield takeLatest(actions.sendShareInvite.started, sendShareInviteSaga);
  yield takeLatest(actions.deleteSharePartner.started, deleteSharePartnerSaga);
  yield takeLatest(actions.leaveInvitation.started, deleteSharePartnerSaga);
  yield takeLatest(actions.deleteSharePartner.done, checkSharesSaga);
  yield takeLatest(actions.leaveInvitation.done, checkSharesSaga);
  yield takeLatest(actions.editInvite.started, editInviteSaga);
  yield takeLatest(actions.acceptInvitation.started, acceptInvitationSaga);
  yield takeLatest(actions.acceptInvitation.failed, getSharesSaga);
  yield takeLatest(actions.acceptInvitation.done, getSharesSaga);
}
